import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Avatar,
  Button,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  Chip,
  Tooltip,
  CircularProgress
} from "@material-ui/core";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import DevicesOtherOutlinedIcon from "@material-ui/icons/DevicesOtherOutlined";
import LanguageOutlinedIcon from "@material-ui/icons/LanguageOutlined";
import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import MailOutlineOutlinedIcon from "@material-ui/icons/MailOutlineOutlined";

import api from "../../services/api";
import withMediaToken from "../../services/mediaUrl";
import toastError from "../../errors/toastError";
import useConfirm from "../../hooks/useConfirm";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.up("md")]: { padding: theme.spacing(4, 6) }
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  avatarBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  avatar: {
    width: 96,
    height: 96,
    fontSize: 32
  },
  avatarHelp: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: 4
  },
  hiddenInput: { display: "none" },
  infoRow: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  infoLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: 0.5
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  notifyRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0)
  },
  notifyDesc: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  securityRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 0)
  },
  sessionItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(1)
  },
  dangerBtn: {
    color: theme.palette.error.main
  }
}));

const TIMEZONES = [
  { value: "America/Sao_Paulo", label: "(UTC-03:00) São Paulo, Brasília" },
  { value: "America/Manaus", label: "(UTC-04:00) Manaus" },
  { value: "America/Rio_Branco", label: "(UTC-05:00) Rio Branco" },
  { value: "America/Noronha", label: "(UTC-02:00) Fernando de Noronha" },
  { value: "UTC", label: "(UTC+00:00) UTC" },
  { value: "Europe/Lisbon", label: "(UTC+00:00) Lisboa" },
  { value: "Europe/Madrid", label: "(UTC+01:00) Madrid" },
  { value: "America/New_York", label: "(UTC-05:00) Nova York" },
  { value: "America/Los_Angeles", label: "(UTC-08:00) Los Angeles" }
];

const LANGUAGES = [
  { value: "pt-BR", label: "🇧🇷 Português (Brasil)" },
  { value: "en", label: "🇺🇸 English" },
  { value: "es", label: "🇪🇸 Español" }
];

const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "DD/MM/AAAA" },
  { value: "MM/DD/YYYY", label: "MM/DD/AAAA" },
  { value: "YYYY-MM-DD", label: "AAAA-MM-DD" }
];

const TIME_FORMATS = [
  { value: "24h", label: "24 horas (14:30)" },
  { value: "12h", label: "12 horas (02:30 PM)" }
];

const NOTIFICATIONS = [
  {
    key: "notifyNewClients",
    label: "Novos Clientes",
    desc: "Receba e-mail quando novos clientes forem cadastrados"
  },
  {
    key: "notifyNewContracts",
    label: "Novos Contratos",
    desc: "Receba e-mail quando novos contratos forem gerados"
  },
  {
    key: "notifyOverdue",
    label: "Cobranças em Atraso",
    desc: "Receba e-mail sobre cobranças vencidas"
  },
  {
    key: "notifyTaskReminders",
    label: "Lembretes de Tarefas",
    desc: "Receba e-mail com lembretes de tarefas e follow-ups"
  },
  {
    key: "notifyMentions",
    label: "Menções e Comentários",
    desc: "Receba e-mail quando for mencionado ou houver novos comentários"
  }
];

const formatDate = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR");
};

const formatDateTime = value => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR");
};

const initialsOf = name => {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0])
    .join("")
    .toUpperCase();
};

const Profile = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const { user: authUser, setUser: setAuthUser } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    timezone: "America/Sao_Paulo",
    language: "pt-BR",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "24h",
    notifyNewClients: false,
    notifyNewContracts: false,
    notifyOverdue: false,
    notifyTaskReminders: true,
    notifyMentions: true,
    sidebarCollapsed: false,
    darkMode: false
  });

  const [pwd, setPwd] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/profile");
        if (!active) return;
        setProfile(data);
        setForm(prev => ({
          ...prev,
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          timezone: data.timezone || "America/Sao_Paulo",
          language: data.language || "pt-BR",
          dateFormat: data.dateFormat || "DD/MM/YYYY",
          timeFormat: data.timeFormat || "24h",
          notifyNewClients: !!data.notifyNewClients,
          notifyNewContracts: !!data.notifyNewContracts,
          notifyOverdue: !!data.notifyOverdue,
          notifyTaskReminders: !!data.notifyTaskReminders,
          notifyMentions: !!data.notifyMentions,
          sidebarCollapsed: !!data.sidebarCollapsed,
          darkMode: !!data.darkMode
        }));
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const handleChange = field => e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePwdChange = field => e =>
    setPwd(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setProfile(data);
      if (setAuthUser) setAuthUser(prev => ({ ...(prev || {}), ...data }));
      toast.success("Perfil atualizado com sucesso.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwd.currentPassword || !pwd.newPassword || !pwd.confirmPassword) {
      toast.error("Preencha todos os campos de senha.");
      return;
    }
    if (pwd.newPassword.length < 6) {
      toast.error("A nova senha deve ter ao menos 6 caracteres.");
      return;
    }
    if (pwd.newPassword !== pwd.confirmPassword) {
      toast.error("A confirmação não corresponde à nova senha.");
      return;
    }
    setSavingPwd(true);
    try {
      await api.put("/profile/password", {
        currentPassword: pwd.currentPassword,
        newPassword: pwd.newPassword
      });
      toast.success("Senha alterada. Você pode precisar entrar novamente.");
      setPwd({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setProfile(prev =>
        prev ? { ...prev, passwordChangedAt: new Date().toISOString() } : prev
      );
    } catch (err) {
      toastError(err);
    } finally {
      setSavingPwd(false);
    }
  };

  const handleAvatarPick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarUpload = async e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("O avatar deve ter no máximo 2MB.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      toast.error("Formato inválido. Use JPG, PNG ou GIF.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      const { data } = await api.post("/profile/avatar", fd);
      setProfile(data);
      if (setAuthUser) setAuthUser(prev => ({ ...(prev || {}), ...data }));
      toast.success("Avatar atualizado.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!profile?.avatarUrl) return;
    if (!(await confirm({ title: "Remover o avatar atual?", message: "Sua foto volta a ser a inicial do nome." }))) return;
    try {
      const { data } = await api.delete("/profile/avatar");
      setProfile(data);
      if (setAuthUser) setAuthUser(prev => ({ ...(prev || {}), ...data }));
    } catch (err) {
      toastError(err);
    }
  };

  if (loading || !profile) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 64
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  // O backend ja devolve a URL absoluta — antes o avatar vinha como caminho
  // relativo e a base era colada aqui. Agora a foto pode estar no bucket, e
  // nesse caso a rota exige o token na query, como toda midia autenticada.
  const avatarSrc = withMediaToken(profile.avatarUrl) || undefined;

  return (
    <Container className={classes.root} maxWidth="md">
      {confirmDialog}
      <Typography variant="h5" gutterBottom>
        Meu Perfil
      </Typography>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <PhotoCameraOutlinedIcon fontSize="small" /> Avatar
        </Typography>
        <div className={classes.avatarBox}>
          <Avatar src={avatarSrc} className={classes.avatar}>
            {initialsOf(profile.name)}
          </Avatar>
          <div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif"
              ref={fileInputRef}
              className={classes.hiddenInput}
              onChange={handleAvatarUpload}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleAvatarPick}
              disabled={uploading}
            >
              {uploading ? "Enviando…" : "Alterar"}
            </Button>{" "}
            {profile.avatarUrl && (
              <Button
                size="small"
                className={classes.dangerBtn}
                onClick={handleAvatarRemove}
                startIcon={<DeleteOutlineIcon />}
              >
                Remover
              </Button>
            )}
            <div className={classes.avatarHelp}>
              JPG, PNG ou GIF. Máximo 2MB.
            </div>
          </div>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>Informações</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Função</span>
              <Chip
                size="small"
                label={(profile.profile || "user").toUpperCase()}
                color="primary"
                variant="outlined"
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Status</span>
              <Chip
                size="small"
                label={profile.status === "active" ? "Active" : profile.status}
                style={{
                  backgroundColor:
                    profile.status === "active" ? "#43a047" : undefined,
                  color: profile.status === "active" ? "#fff" : undefined
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Último Login</span>
              <span>{formatDateTime(profile.lastLoginAt)}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={4}>
            <div className={classes.infoRow}>
              <span className={classes.infoLabel}>Membro desde</span>
              <span>{formatDate(profile.createdAt)}</span>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          Dados Pessoais
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome Completo *"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail *"
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Telefone"
              value={form.phone}
              onChange={handleChange("phone")}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando…" : "Salvar"}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <LockOutlinedIcon fontSize="small" /> Alterar Senha
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Senha Atual *"
              type="password"
              value={pwd.currentPassword}
              onChange={handlePwdChange("currentPassword")}
              fullWidth
              variant="outlined"
              size="small"
              autoComplete="current-password"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Nova Senha *"
              type="password"
              value={pwd.newPassword}
              onChange={handlePwdChange("newPassword")}
              fullWidth
              variant="outlined"
              size="small"
              helperText="Mínimo 6 caracteres"
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Confirmar Senha *"
              type="password"
              value={pwd.confirmPassword}
              onChange={handlePwdChange("confirmPassword")}
              fullWidth
              variant="outlined"
              size="small"
              autoComplete="new-password"
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleChangePassword}
            disabled={savingPwd}
          >
            {savingPwd ? "Alterando…" : "Alterar Senha"}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          Preferências do Sistema
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Fuso Horário"
              value={form.timezone}
              onChange={handleChange("timezone")}
              fullWidth
              variant="outlined"
              size="small"
              helperText="Fuso horário usado para exibir datas e horários"
              InputProps={{
                startAdornment: (
                  <AccessTimeOutlinedIcon
                    style={{ marginRight: 8, opacity: 0.6 }}
                    fontSize="small"
                  />
                )
              }}
            >
              {TIMEZONES.map(tz => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Idioma"
              value={form.language}
              onChange={handleChange("language")}
              fullWidth
              variant="outlined"
              size="small"
              helperText="Idioma da interface do sistema"
              InputProps={{
                startAdornment: (
                  <LanguageOutlinedIcon
                    style={{ marginRight: 8, opacity: 0.6 }}
                    fontSize="small"
                  />
                )
              }}
            >
              {LANGUAGES.map(l => (
                <MenuItem key={l.value} value={l.value}>
                  {l.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Formato de Data"
              value={form.dateFormat}
              onChange={handleChange("dateFormat")}
              fullWidth
              variant="outlined"
              size="small"
              helperText="Formato usado para exibir datas no sistema"
              InputProps={{
                startAdornment: (
                  <EventOutlinedIcon
                    style={{ marginRight: 8, opacity: 0.6 }}
                    fontSize="small"
                  />
                )
              }}
            >
              {DATE_FORMATS.map(d => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Formato de Hora"
              value={form.timeFormat}
              onChange={handleChange("timeFormat")}
              fullWidth
              variant="outlined"
              size="small"
              helperText="Formato usado para exibir horários"
              InputProps={{
                startAdornment: (
                  <ScheduleOutlinedIcon
                    style={{ marginRight: 8, opacity: 0.6 }}
                    fontSize="small"
                  />
                )
              }}
            >
              {TIME_FORMATS.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando…" : "Salvar Preferências"}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          Interface
        </Typography>
        <div className={classes.notifyRow}>
          <Switch
            color="primary"
            checked={!!form.sidebarCollapsed}
            onChange={handleChange("sidebarCollapsed")}
          />
          <div>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Iniciar com barra lateral recolhida
            </Typography>
            <span className={classes.notifyDesc}>
              Mostra apenas os ícones do menu lateral por padrão.
            </span>
          </div>
        </div>
        <div className={classes.notifyRow}>
          <Switch
            color="primary"
            checked={!!form.darkMode}
            onChange={handleChange("darkMode")}
          />
          <div>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Modo escuro
            </Typography>
            <span className={classes.notifyDesc}>
              Aplica a paleta escura na interface.
            </span>
          </div>
        </div>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando…" : "Salvar Interface"}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <MailOutlineOutlinedIcon fontSize="small" /> Notificações por E-mail
        </Typography>
        {NOTIFICATIONS.map(n => (
          <div key={n.key} className={classes.notifyRow}>
            <Switch
              color="primary"
              checked={!!form[n.key]}
              onChange={handleChange(n.key)}
            />
            <div>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {n.label}
              </Typography>
              <span className={classes.notifyDesc}>{n.desc}</span>
            </div>
          </div>
        ))}
        <Typography className={classes.helperText}>
          O disparo automático de e-mails depende da configuração de automação
          do módulo.
        </Typography>
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Salvando…" : "Salvar Notificações"}
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <SecurityOutlinedIcon fontSize="small" /> Segurança
        </Typography>

        <div className={classes.securityRow}>
          <div>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Autenticação em Dois Fatores (2FA)
            </Typography>
            <span className={classes.notifyDesc}>
              Adicione uma camada extra de segurança
            </span>
          </div>
          <Tooltip title="Funcionalidade em breve">
            <span>
              <Button variant="outlined" size="small" disabled>
                Em breve
              </Button>
            </span>
          </Tooltip>
        </div>
        <Divider />
        <div className={classes.securityRow}>
          <div>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Última atualização de senha
            </Typography>
            <span className={classes.notifyDesc}>
              {profile.passwordChangedAt
                ? formatDateTime(profile.passwordChangedAt)
                : "Nunca alterada"}
            </span>
          </div>
        </div>
      </Paper>

      <Paper className={classes.paper}>
        <Typography className={classes.sectionTitle}>
          <DevicesOtherOutlinedIcon fontSize="small" /> Sessões Ativas
        </Typography>
        <div className={classes.sessionItem}>
          <DevicesOtherOutlinedIcon />
          <div style={{ flex: 1 }}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Sessão Atual
            </Typography>
            <span className={classes.notifyDesc}>
              {navigator.platform || "Navegador"} •{" "}
              {formatDateTime(profile.lastLoginAt || new Date())}
            </span>
          </div>
          <Chip size="small" label="Ativa" color="primary" />
        </div>
        <Typography className={classes.helperText}>
          Sua sessão atual permanecerá ativa.
        </Typography>
      </Paper>

      {authUser && authUser.id !== profile.id && null}
    </Container>
  );
};

export default Profile;
